// src/components/orders/RefundModal.js
// ============================================================
// REMBOURSEMENTS — demande, décision, annulation
// ------------------------------------------------------------
// Un seul composant pour trois gestes qui n'engagent pas les
// mêmes personnes :
//
//   'request' — le commercial DEMANDE. Motif obligatoire.
//               Rien ne bouge : ni l'argent, ni le coupon.
//   'decide'  — l'admin tranche. C'est le seul moment où le
//               remboursement a réellement lieu.
//   'undo'    — l'admin corrige un remboursement saisi PAR
//               ERREUR, quand l'argent n'est jamais sorti.
//
// 'undo' ne reprend PAS de l'argent déjà rendu au client. Si les
// 500 MAD ont quitté la caisse, aucun bouton ne les fait revenir.
// Le texte de l'écran le dit, pour qu'on ne s'y trompe pas.
//
// Maxula — AbodyStudio Limited
// ============================================================

import React, { useState } from 'react';
import { FiX, FiAlertTriangle, FiRotateCcw, FiCheck, FiSend } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { orderAPI } from '../../services/api';

export default function RefundModal({ mode, order, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const total = Number(order?.total || 0).toFixed(2);
  const num = order?.orderNumber || '';

  const run = async (approve) => {
    const txt = note.trim();
    if ((mode === 'request' || mode === 'undo' || (mode === 'decide' && !approve)) && txt.length < 5) {
      toast.error('Expliquez en quelques mots la raison.');
      return;
    }
    setBusy(true);
    try {
      let r;
      if (mode === 'request') r = await orderAPI.requestRefund(order.id ?? order._id, txt);
      else if (mode === 'undo') r = await orderAPI.undoRefund(order.id ?? order._id, txt);
      else r = await orderAPI.decideRefund(order.id ?? order._id, approve, txt);
      toast.success(r?.data?.message || 'Enregistré');
      onDone && onDone();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Opération refusée');
    } finally {
      setBusy(false);
    }
  };

  const titre = mode === 'request' ? 'Demander un remboursement'
    : mode === 'undo' ? 'Annuler le remboursement'
    : 'Décider du remboursement';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-lg font-bold">{titre}</h2>
            <p className="text-sm text-gray-500 font-mono">{num} · {total} MAD</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><FiX size={22} /></button>
        </div>

        <div className="p-5 space-y-4">
          {mode === 'request' && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
              Votre demande part à l'administrateur. <strong>Rien n'est remboursé
              pour l'instant</strong> : le coupon du client reste valable tant
              qu'il n'a pas validé.
            </div>
          )}

          {mode === 'decide' && (
            <>
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <div className="flex items-start gap-2">
                  <FiAlertTriangle className="mt-0.5 shrink-0" size={16} />
                  <div>
                    Approuver annule les coupons, rend le stock et retire au
                    partenaire les gains de cette vente.
                  </div>
                </div>
              </div>
              {order?.refundReason && (
                <div className="text-sm">
                  <div className="text-xs text-gray-500 mb-1">
                    Motif invoqué{order?.refundRequestedBy ? ` par ${order.refundRequestedBy.name}` : ''} :
                  </div>
                  <div className="border rounded-lg px-3 py-2 bg-gray-50 text-gray-800">{order.refundReason}</div>
                </div>
              )}
            </>
          )}

          {mode === 'undo' && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <div className="flex items-start gap-2">
                <FiAlertTriangle className="mt-0.5 shrink-0" size={16} />
                <div>
                  À utiliser uniquement si <strong>l'argent n'est jamais sorti de
                  la caisse</strong>. Cette action ne reprend rien à un client
                  déjà remboursé.
                  <div className="mt-2">
                    La commande redevient payée, le stock est repris et de
                    <strong> nouveaux coupons</strong> sont émis — l'ancien code
                    du client ne fonctionnera plus.
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs text-gray-500 mb-1">
              {mode === 'decide' ? 'Note (obligatoire en cas de refus)' : 'Raison (obligatoire)'}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={mode === 'undo'
                ? 'Ex : marquée remboursée par erreur, mauvaise commande'
                : 'Ex : client absent, prestation non réalisée'}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div className="border-t p-4 flex flex-wrap justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy}
            className="px-4 py-2 rounded-lg border text-sm font-medium hover:bg-gray-50">Fermer</button>

          {mode === 'request' && (
            <button type="button" onClick={() => run(true)} disabled={busy}
              className="px-5 py-2 rounded-lg bg-[#e11d7b] text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2">
              <FiSend size={15} /> {busy ? 'Envoi…' : 'Envoyer la demande'}
            </button>
          )}

          {mode === 'undo' && (
            <button type="button" onClick={() => run(true)} disabled={busy}
              className="px-5 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2">
              <FiRotateCcw size={15} /> {busy ? 'En cours…' : 'Annuler le remboursement'}
            </button>
          )}

          {mode === 'decide' && (
            <>
              <button type="button" onClick={() => run(false)} disabled={busy}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold disabled:opacity-60">
                Refuser
              </button>
              <button type="button" onClick={() => run(true)} disabled={busy}
                className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2">
                <FiCheck size={15} /> {busy ? 'En cours…' : 'Approuver le remboursement'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
